<script lang="ts">
export default {
  name: 'Notifications',
};
</script>

<script setup lang="ts">
import { MenuSquare, Bell, X } from '@lucide/vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { AppState, Commit } from '@/store';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import notificationService from '@/services/v2/NotificationService';
import { relativeDateTime } from '@/utilities/Dates';
import { StoreMutationType } from '@/types/StoreMutationType';
import axios from 'axios';
import { SessionExpiredError } from '@/composables/useSessionExpiry';
import { compactNumber } from '@/utilities/Number.js';
import { sanitizeHtml } from '@/utilities/Sanitization';
import { routeFromNotification } from '@/utilities/NotificationRouting';

const MAX_VIEWABLE_NOTIFICATIONS = 5;

// ---------
// refs
// ---------

const store = useStore<AppState>();
const router = useRouter();
const intervalId = ref(0);
const notificationRef = ref<HTMLElement | null>(null);
const notificationListRef = ref<HTMLElement | null>(null);
const isMenuOpen = ref(false);
const notificationsModifiedDate = ref<Date>(new Date());

// ---------
// computed
// ---------

const notifications = computed(() => store.state.notifications.slice(0, MAX_VIEWABLE_NOTIFICATIONS));
const totalNotificationCount = computed(() => store.state.notifications.length);
const readNotificationCount = computed(() => compactNumber(store.state.notifications.filter((n) => !n.isRead).length));
const hasNotifications = computed(() => store.state.notifications.filter((n) => !n.isRead).length > 0);

// ---------
// lifecycle
// ---------

onMounted(async () => {
  try {
    const initial = await notificationService.getNotifications();
    Commit(StoreMutationType.SET_NOTIFICATIONS, initial.notifications);
    notificationsModifiedDate.value = new Date();
  } catch (e) {
    // Mounting behind the session-expired modal must not surface an unhandled rejection.
    if (!(e instanceof SessionExpiredError)) {
      console.error(e);
    }
    return;
  }
  await notificationPoll();
});

onUnmounted(stopPolling);

function stopPolling(): void {
  if (intervalId.value) {
    window.clearInterval(intervalId.value);
    intervalId.value = 0;
  }
}

async function notificationPoll() {
  intervalId.value = window.setInterval(async function () {
    try {
      const { notifications } = await notificationService.getNotifications({ ifModifiedSince: notificationsModifiedDate.value });
      notificationsModifiedDate.value = new Date();

      if (notifications.sort().join() !== store.state.notifications.sort().join()) {
        Commit(StoreMutationType.SET_NOTIFICATIONS, notifications);
      }
    } catch (e) {
      if (e instanceof SessionExpiredError) {
        // The modal is up and every further request would be refused locally.
        stopPolling();
      } else if (axios.isAxiosError(e) && e.response?.status === 304) {
        // 304 Not Modified — no new notifications, nothing to do
      } else if (axios.isAxiosError(e) && e.response?.status && e.response.status >= 400 && e.response.status <= 599) {
        // Stop polling on client errors (401, 403, 404, etc.) and server errors (500, 502, 503, etc.)
        console.info('Stopping notification polling due to error:', e.response.status);
        stopPolling();
      } else {
        console.error(e);
      }
    }
  }, 600_000);
}

// ---------
// watchers
// ---------
watch(isMenuOpen, (isOpen) => {
  if (!isOpen) {
    blurActiveElement();
  }
});

// ----------
// functions
// ----------

function blurActiveElement() {
  const activeElement = document.activeElement as HTMLElement;
  activeElement.blur();
}

function toggleMenuClick() {
  isMenuOpen.value = !isMenuOpen.value;
}

async function handleNotificationDelete(id: number) {
  try {
    await notificationService.deleteNotification(id);
    Commit(
      StoreMutationType.SET_NOTIFICATIONS,
      store.state.notifications.filter((n) => n.id !== id),
    );
  } catch (e) {
    console.error(e);
  }
}

async function handleNotificationClick(id: number) {
  const notification = notifications.value.find((n) => n.id === id);
  if (!notification) {
    return;
  }

  if (!notification.isRead) {
    try {
      await notificationService.markAsRead(id);
      Commit(
        StoreMutationType.SET_NOTIFICATIONS,
        store.state.notifications.map((n) => {
          if (n.id === id) {
            n.isRead = true;
          }
          return n;
        }),
      );
    } catch (e) {
      console.error(e);
    }
  }

  notificationRef.value?.blur();
  notificationListRef.value?.blur();

  await routeFromNotification(router, notification, { forceRouteRefresh: true });
}

function handleViewAllClick() {
  notificationRef.value?.blur();
  notificationListRef.value?.blur();
  router.push('/notifications');
}

function sanitizeNotificationMessage(message: string): string {
  return sanitizeHtml(message);
}
</script>

<template>
  <div
    class="dropdown-hover dropdown dropdown-end dropdown-bottom ml-2 flex h-full items-center transition-all">
    <div
      ref="notificationRef"
      tabindex="0"
      role="button"
      class="group btn relative border-none btn-ghost btn-lg hover:border-none hover:bg-white/10 hover:shadow-none transition-colors"
      :class="{ 'mr-6' : hasNotifications }"
      @blur="isMenuOpen = false"
      @click="toggleMenuClick">
      <Bell :size="15" class="absolute stroke-white group-hover:rotate-12" />
      <div
        v-visible="hasNotifications"
        class="absolute top-1 left-6 badge border-0 bg-agency-red badge-sm text-white">
        {{ readNotificationCount }}
      </div>
    </div>
    <ul
      v-visible="totalNotificationCount > 0"
      tabindex="0"
      class="dropdown-content menu w-80 rounded-md border border-solid border-gray-200 bg-base-100 p-0 px-1 shadow-lg"
      ref="notificationListRef"
      @blur="isMenuOpen = false">
      <li
        v-for="notification in notifications"
        class="group border-x-0 border-t-1 border-b-0 border-solid border-gray-300 first:border-t-0"
        :key="notification.id"
        ref="notificationItemRefs">
        <a
          class="relative flex rounded-none px-0"
          style="text-wrap: inherit"
          @click="handleNotificationClick(notification.id)">
          <div class="flex w-full flex-col items-start gap-0 px-2">
            <div class="flex items-center justify-stretch gap-2">
              <div v-if="!notification.isRead" class="badge bg-green-400 badge-xs"></div>
              <div :class="{ 'opacity-60': notification.isRead }">
                <p class="line-clamp-2" v-html="sanitizeNotificationMessage(notification.message)"></p>
              </div>
            </div>
            <div class="scale-90 self-end text-xs text-base-content/60">
              {{ relativeDateTime(notification.createdAt) }}
            </div>
          </div>
          <div class="absolute flex h-full w-full justify-end">
            <X
              @click.stop="handleNotificationDelete(notification.id)"
              :size="16"
              class="h-full stroke-black opacity-60 hover:stroke-agency-red hover:opacity-100" />
          </div>
        </a>
      </li>
      <li class="border-x-0 border-t-1 border-b-0 border-solid border-gray-300">
        <a
          @click="handleViewAllClick"
          class="flex items-center justify-center rounded-none px-2 py-1">
          <div class="flex items-center gap-2 p-1">
            <div>View All</div>
            <MenuSquare :size="18" />
          </div>
        </a>
      </li>
    </ul>
    <div
      v-visible="totalNotificationCount === 0"
      tabindex="0"
      class="dropdown-content menu h-80 w-80 rounded-md border border-solid border-gray-200 bg-base-100 p-2 shadow-lg"
      @blur="isMenuOpen = false">
      <div class="m-auto flex flex-col items-center justify-center">
        <span>No new notifications!</span>
        <router-link class="p-1" to="/notifications/preferences">
          <span class="font-bold underline">View Settings</span>
        </router-link>
      </div>
    </div>
  </div>
</template>
